import { useVirtualizer, type Virtualizer } from "@tanstack/react-virtual";
import { type RefObject, useCallback, useMemo, useRef } from "react";
import type { ThumbnailProps } from "react-pdf";

/** The loaded pdf.js document proxy, as react-pdf surfaces it (via `<Document>`'s
 *  `onLoadSuccess` / a `<Page>`/`<Thumbnail>` `pdf` prop). Derived from react-pdf's own types
 *  because `pdfjs-dist` isn't directly resolvable from this package. */
export type PdfDocumentProxy = Exclude<ThumbnailProps["pdf"], false | undefined>;

/** US-Letter portrait aspect ratio (height / width). Used only for the initial
 *  size estimate before a page rasterizes and `measureElement` reports its real
 *  height. */
export const DEFAULT_PDF_PAGE_RATIO = 1.294;

export interface UseVirtualizedPdfPagesArgs {
  /** Number of pages (0 until the `<Document>` reports `numPages`). */
  count: number;
  /** The scroll container that the pages live inside. */
  scrollElementRef: RefObject<HTMLElement | null>;
  /** Estimated rendered height (px, incl. any vertical gap) for a page index. */
  estimateSize: (index: number) => number;
  /** Pages to keep mounted on either side of the visible window. */
  overscan?: number;
  /** Called with the 1-indexed page nearest the viewport center as it scrolls. */
  onVisiblePageChange?: (page: number) => void;
}

export interface VirtualizedPdfPages {
  virtualizer: Virtualizer<HTMLElement, Element>;
  virtualItems: ReturnType<Virtualizer<HTMLElement, Element>["getVirtualItems"]>;
  totalSize: number;
  /** Scroll a 1-indexed page to the top of the viewport (works even when the
   *  target page is currently unmounted). */
  scrollToPage: (page: number) => void;
}

/**
 * Windows react-pdf pages with `@tanstack/react-virtual`: the `<Document>` stays
 * mounted (the file still downloads once) but only the pages near the viewport
 * are rendered. `measureElement` installs a per-item ResizeObserver, so the
 * estimate self-corrects once a page rasterizes (skeleton → real height) or its
 * width changes (zoom/rotate) — no manual remeasure needed.
 *
 * The visible page is derived from the scroll offset rather than an
 * IntersectionObserver, because an IO cannot observe pages that aren't mounted.
 */
export function useVirtualizedPdfPages({
  count,
  scrollElementRef,
  estimateSize,
  overscan = 2,
  onVisiblePageChange,
}: UseVirtualizedPdfPagesArgs): VirtualizedPdfPages {
  // Keep the latest callback without re-creating the virtualizer's onChange.
  const onVisiblePageChangeRef = useRef(onVisiblePageChange);
  onVisiblePageChangeRef.current = onVisiblePageChange;

  const lastReportedPageRef = useRef(0);

  const handleChange = useCallback((instance: Virtualizer<HTMLElement, Element>) => {
    const report = onVisiblePageChangeRef.current;
    if (!report) return;
    const viewport = instance.scrollElement?.clientHeight ?? 0;
    const center = (instance.scrollOffset ?? 0) + viewport / 2;
    const items = instance.getVirtualItems();
    if (items.length === 0) return;
    let best = items[0];
    for (const item of items) {
      if (center >= item.start && center < item.end) {
        best = item;
        break;
      }
      // Fall back to whichever rendered item is closest to the center.
      const bestDist = Math.abs(center - (best.start + best.size / 2));
      const dist = Math.abs(center - (item.start + item.size / 2));
      if (dist < bestDist) best = item;
    }
    const page = best.index + 1;
    if (page !== lastReportedPageRef.current) {
      lastReportedPageRef.current = page;
      report(page);
    }
  }, []);

  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => scrollElementRef.current,
    estimateSize,
    overscan,
    measureElement: (el) => el?.getBoundingClientRect().height ?? 0,
    onChange: handleChange,
  });

  const scrollToPage = useCallback(
    (page: number) => {
      virtualizer.scrollToIndex(Math.max(0, page - 1), { align: "start" });
    },
    [virtualizer]
  );

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  return useMemo(
    () => ({ virtualizer, virtualItems, totalSize, scrollToPage }),
    [virtualizer, virtualItems, totalSize, scrollToPage]
  );
}

export default useVirtualizedPdfPages;
